import {
  AriaTabPanel,
  AriaTabs,
  Box,
  HStack,
  KdsDivider,
  KdsSpinner,
  KdsTab,
  KdsTabList,
  KdsText,
  VStack,
} from "@kazaar-platform/design-system";
import { RiBox3Line } from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { ControlledCombobox } from "@/components/Form";
import { type QualifyTechnicalFieldsFormProps } from "../QualifyDrawerForm.types";
import { QualifyTechnicalFieldsFormConstants } from "./QualifyTechnicalFieldsForm.constants";
import { TechnicalField } from "./TechnicalField";

const QualifyTechnicalFieldsForm = ({
  products,
  fields,
  isLoadingFields,
  onProductChange,
  productComboboxKey,
}: QualifyTechnicalFieldsFormProps) => {
  const { t } = useTranslate();

  const [currentActiveTab, setCurrentActiveTab] = useState(
    QualifyTechnicalFieldsFormConstants.PRODUCT_TAB
  );

  const { control } = useFormContext();

  const productId = useWatch({ control, name: "productId" });

  const productFields = fields?.fields.filter((f) => f.categorie === 1) || [];
  const packingFields = fields?.fields.filter((f) => f.categorie === 2) || [];

  useEffect(() => {
    if (productFields?.length === 0) {
      setCurrentActiveTab(QualifyTechnicalFieldsFormConstants.PACKING_TAB);
    }
    setCurrentActiveTab(
      productFields?.length === 0 && packingFields?.length > 0
        ? QualifyTechnicalFieldsFormConstants.PACKING_TAB
        : QualifyTechnicalFieldsFormConstants.PRODUCT_TAB
    );
  }, [fields]);

  return (
    <Box
      maxH="100%"
      overflowY="auto"
      p="sp-md"
      bgColor="elevation.second"
      w="full"
      rounded="br-sm"
      minH="100%"
    >
      <VStack gap="sp-md">
        <KdsText size="title-sm">
          {t("expertMode.qualifyDrawer.technicalSpecs.title")}
        </KdsText>

        <HStack gap="sp-sm">
          <ControlledCombobox
            key={`product-${productComboboxKey}`}
            iconLeft={RiBox3Line}
            name="productId"
            placeholder={t(
              "expertMode.qualifyDrawer.technicalSpecs.productPlaceholder"
            )}
            options={
              products?.map((e) => ({
                label: e.name,
                name: e.name || "",
                id: e.product_id,
                textValue: `${e.product_id}`,
              })) || []
            }
            onSelectionChange={onProductChange}
          />

          <ControlledCombobox
            key={`deliverable-${productId}`}
            name="deliverableId"
            placeholder={t(
              "expertMode.qualifyDrawer.technicalSpecs.deliverablePlaceholder"
            )}
            isDisabled={!fields?.deliverables?.length}
            options={
              fields?.deliverables?.map((_e) => ({
                label: _e.name,
                name: _e.name,
                id: _e.deliverable_id,
                textValue: `${_e.deliverable_id}`,
              })) || []
            }
          />
        </HStack>

        <KdsDivider />

        {!fields?.fields?.length && !isLoadingFields && (
          <Box bgColor="surface.second" p="sp-md">
            <HStack justify="center">
              <KdsText size="subtitle-sm" color="text-bodyvariant">
                {t(
                  "expertMode.qualifyDrawer.technicalSpecs.selectOptionHelper"
                )}
              </KdsText>
            </HStack>
          </Box>
        )}

        {isLoadingFields && (
          <Box w="full">
            <HStack justify="center">
              <KdsSpinner size="xl" />
            </HStack>
          </Box>
        )}

        {!isLoadingFields && !!fields?.fields.length && (
          <AriaTabs
            selectedKey={currentActiveTab}
            onSelectionChange={(key) => setCurrentActiveTab(String(key))}
          >
            <KdsTabList fullWidth withBorder alignLeft>
              <KdsTab
                id={QualifyTechnicalFieldsFormConstants.PRODUCT_TAB}
                label={t(
                  "expertMode.qualifyDrawer.technicalSpecs.tabs.product"
                )}
              />
              <KdsTab
                id={QualifyTechnicalFieldsFormConstants.PACKING_TAB}
                label={t(
                  "expertMode.qualifyDrawer.technicalSpecs.tabs.packing"
                )}
              />
            </KdsTabList>

            <AriaTabPanel id={QualifyTechnicalFieldsFormConstants.PRODUCT_TAB}>
              <div className="grid grid-cols-2 gap-sp-sm p-sp-xs">
                {!!productFields?.length &&
                  productFields.map((field) => (
                    <TechnicalField key={field.id} field={field} />
                  ))}
                {!productFields?.length && (
                  <KdsText>
                    {t(
                      "expertMode.qualifyDrawer.technicalSpecs.tabs.noProductFields"
                    )}
                  </KdsText>
                )}
              </div>
            </AriaTabPanel>
            <AriaTabPanel id={QualifyTechnicalFieldsFormConstants.PACKING_TAB}>
              <div className="grid grid-cols-2 gap-sp-sm p-sp-xs">
                {!!packingFields?.length &&
                  packingFields.map((field) => (
                    <TechnicalField key={field.id} field={field} />
                  ))}
                {!packingFields?.length && (
                  <KdsText>
                    {t(
                      "expertMode.qualifyDrawer.technicalSpecs.tabs.noPackingFields"
                    )}
                  </KdsText>
                )}
              </div>
            </AriaTabPanel>
          </AriaTabs>
        )}
      </VStack>
    </Box>
  );
};

export { QualifyTechnicalFieldsForm };
